// @flow
import React, { useCallback, useState, useEffect } from "react";
import Constants from "data/constants";
import { getLocalizedText } from "data/utils";
import GridList from "@hs/grid-list";
import noop from "lodash/noop";
import ContextMenuHandler from "components/ContextMenu/Handler";
import CellContent from "./CellContent";
import { isHandheld } from "data/bowser";
import {
    GridListConstant,
    SortDirectionOptions,
    SORT_BUCKET_NAME,
} from "data/types";
import localForage from "localforage";

const SearchResultGrid = ({
    result,
    rowCount,
    onClick,
    onMoreClick,
    onContextClick,
    onFilterBarSelect,
    activeRowIndex,
    scrollToRow,
    loadMoreRows,
    children,
    searchRequest,
    onSetActiveRowIndex,
    terms,
    isMoreHidden,
    handleMoreRowsLoaded,
    isRowActive,
    refresh,
    columnMapper,
    onReorder,
    colWidths: colWidthsFromProps,
}) => {
    const { aggregations, rows: rowsFromResult, totalCount, cols } = result;
    const [rows, setRows] = useState(rowsFromResult);
    const [colWidths, setColWidths] = useState(colWidthsFromProps);

    useEffect(() => {
        setRows(rowsFromResult);
    }, [rowsFromResult]);

    useEffect(() => {
        setColWidths(colWidthsFromProps);
    }, [colWidthsFromProps]);

    const [pagesStatus, setPagesStatus] = useState([Constants.OK]);
    const { pageSize } = result;
    /* istanbul ignore next */
    const count = totalCount === 0 ? Object.keys(rows).length : totalCount;
    const parseCellColumnTitle = useCallback((header, content) => {
        const value = content && content.value;
        return value ? value.value || value : value;
    }, []);
    const isRowLoaded = useCallback(
        ({ rowIndex }) => Object.prototype.hasOwnProperty.call(rows, rowIndex),
        [rows]
    );
    const isColumnFiltered = useCallback(
        (name) =>
            aggregations.find((f) => f.name === name && f.selected != null),
        [aggregations]
    );
    const isColumnSorted = useCallback(
        (name) => {
            const sort = aggregations.find((f) => f.name === SORT_BUCKET_NAME);
            return sort && sort.selected.includes(name);
        },
        [aggregations]
    );
    const getColumnSortDirection = useCallback(
        () =>
            (searchRequest.sorts &&
                searchRequest.sorts[0] &&
                searchRequest.sorts[0].direction) ||
            SortDirectionOptions.Ascending,
        [searchRequest.sorts]
    );
    const handleLoadMoreRows = useCallback(
        ({ rowIndex }) => {
            let pageNum = 0;
            /* istanbul ignore else */
            if (rowIndex > 0) {
                pageNum = Math.floor(rowIndex / pageSize);
            }

            // page already loaded/requested?
            /* istanbul ignore else */
            if (
                pagesStatus[pageNum] === Constants.OK ||
                pagesStatus[pageNum] === Constants.LOADING
            )
                return null;
            setPagesStatus((pagesStatus) => {
                pagesStatus[pageNum] = Constants.LOADING;
                return pagesStatus;
            });
            loadMoreRows(pageNum)
                .then((result) => {
                    setPagesStatus((pagesStatus) => {
                        pagesStatus[pageNum] = Constants.OK;
                        return pagesStatus;
                    });
                    setRows((rows) => {
                        const nextRows = { ...rows, ...result.rows };
                        handleMoreRowsLoaded(nextRows, result.totalCount);
                        return nextRows;
                    });
                })
                .catch((e) => {
                    setPagesStatus((pagesStatus) => {
                        pagesStatus[pageNum] = Constants.ERROR;
                        return pagesStatus;
                    });
                });
        },
        [handleMoreRowsLoaded, pagesStatus, pageSize, loadMoreRows]
    );
    const handleCellClick = useCallback(
        (e, rowIndex: number, columnIndex: number, itemUri: string) => {
            onClick(e, rowIndex, rows[rowIndex]);
        },
        [onClick, rows]
    );
    const handleCellMoreClick = useCallback(
        (e, itemUri, rowIndex) => {
            const row = rows[rowIndex];
            onMoreClick(e, rowIndex, row);
        },
        [onMoreClick, rows]
    );
    const handleCellContextClick = useCallback(
        (e, rowIndex: number, columnIndex: number, itemUri: string) => {
            const row = rows[rowIndex];
            onContextClick(e, rowIndex, row);
        },
        [onContextClick, rows]
    );
    const handleHeaderFilterClick = useCallback(
        (e, name) => {
            e.stopPropagation();
            const filter = aggregations.find((f) => f.name === name);
            onFilterBarSelect(name, filter.valueType, "__ALL__");
        },
        [aggregations, onFilterBarSelect]
    );
    const handleHeaderClick = useCallback(
        (columnIndex: number, header: string) => {
            const col = cols[columnIndex];
            if (!col.canSort || col.type == null) return;
            onFilterBarSelect(SORT_BUCKET_NAME, col.type, col.name);
        },
        [onFilterBarSelect, cols]
    );
    const getColumnWidth = useCallback(
        ({ index, col }: { index: number, col: DocColumnResultDTO }) => {
            if (colWidths?.[col.name]) {
                return colWidths[col.name];
            }

            const width =
                index === 0 ? (isHandheld() ? 35 : 42) : col.width * 1.2 + 15;
            return width + (index === cols.length - 1 ? 20 : 0);
        },
        [cols.length, colWidths]
    );

    const onResize = useCallback(
        (index: number, widthChange: number) => {
            let newWidth = Math.max(
                getColumnWidth({ index, col: cols[index] }) + widthChange,
                30
            );

            setColWidths((colWidths) => {
                const newColWidths = { ...colWidths };
                newColWidths[cols[index].name] = newWidth;
                return newColWidths;
            });

            localForage
                .getItem(`searchResultLayoutColumns_${btoa(searchRequest.id)}`)
                .then((item) => {
                    if (item == null) {
                        item = {};
                    }
                    item[cols[index].name] = newWidth;
                    localForage.setItem(
                        `searchResultLayoutColumns_${btoa(searchRequest.id)}`,
                        item
                    );
                });
        },
        [cols, getColumnWidth, searchRequest.id]
    );

    const getCell = useCallback(
        ({ row, columnIndex }) => {
            const col = cols[columnIndex];
            const foundTerms = row.foundTerms || terms;
            switch (col.name) {
                case GridListConstant.ExtensionIcon:
                case GridListConstant.ItemUri:
                    return { ...row, foundTerms };
                case "Dateityp":
                    return { name: col.name, value: row.ext, foundTerms };
                default:
                    const cell = row.attributes.find(
                        (a) => a.name === col.name
                    );
                    // show title instead of name, if empty
                    if (col.name === "Name" && cell == null) {
                        return { name: col.name, value: row.title, foundTerms };
                    }
                    // if (cell == null) console.log(col, cell, row);
                    return cell ? { ...cell, foundTerms } : null;
            }
        },
        [cols, terms]
    );
    return (
        <GridList
            cols={cols}
            rows={rows}
            columnCount={cols.length - 1}
            rowCount={count}
            getColumnWidth={getColumnWidth}
            CellContent={CellContent}
            ContextMenuHandler={ContextMenuHandler}
            parseCellHeaderCaption={getLocalizedText}
            parseCellColumnTitle={parseCellColumnTitle}
            isRowLoaded={isRowLoaded}
            canColumnFilter={isColumnFiltered}
            isColumnFiltered={isColumnFiltered}
            isColumnSorted={isColumnSorted}
            getColumnSortDirection={getColumnSortDirection}
            rowStartIndexUpdated={noop}
            loadMoreRows={handleLoadMoreRows}
            onCellClick={handleCellClick}
            onCellDoubleClick={noop}
            onCellMoreClick={handleCellMoreClick}
            onContextClick={handleCellContextClick}
            onHeaderClick={handleHeaderClick}
            onHeaderFilterClick={handleHeaderFilterClick}
            activeRowIndex={activeRowIndex}
            scrollToRow={scrollToRow}
            getCell={getCell}
            children={children}
            headerFilterTitle={getLocalizedText("$common:filter.reset")}
            actionsDisabled={isMoreHidden}
            isRowActive={isRowActive}
            refresh={refresh}
            onResize={onResize}
            columnMapper={columnMapper}
            onReorder={onReorder}
        />
    );
};
SearchResultGrid.displayName = "SearchResult.Grid";

export default SearchResultGrid;
